import { useAuth } from "@dl/web-core-js/auth";
import { Button, Card, Field, Input, Select, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { refusalMessage, REPLENISHMENT_POLICIES, USAGE_SOURCES, type PartDTO, type ReplenishmentPolicy, type UsageSource } from "../../api/manufacturing";
import { PartReplenishmentClient, ReplenishmentClient, replenishmentKeys, type PartReplenishmentDTO } from "../../api/replenishment";
import { MaterialStory, policyLabel } from "../purchasing/replenishment/MaterialStory";
import { isoDate } from "../../api/reporting";

/**
 * A purchased part's replenishment: its story on the left, its own settings
 * on the right.
 *
 * <p>A part follows its group — policy, usage source, safety days — until it
 * is given a figure of its own here. Each blank field says what it falls back
 * to, so "blank" never reads as "zero". Saving does not replan: the next run
 * does, and "Recalculate now" runs it at once.</p>
 */
export function PartReplenishmentTab({ part, readOnly }: { part: PartDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const replenishment = useMemo(() => new ReplenishmentClient(http), [http]);
  const overrides = useMemo(() => new PartReplenishmentClient(http), [http]);
  const key = replenishmentKeys.part(part.id);

  const detail = useQuery({ queryKey: key, queryFn: () => replenishment.part(part.id), staleTime: 30_000 });
  const saved = detail.data?.settings ?? null;
  // Until the saved settings arrive the form would show blanks that are not
  // the part's, and an edit made then would be overwritten when they land.
  const locked = readOnly || detail.isLoading;

  const [draft, setDraft] = useState<PartReplenishmentDTO>(() => blank(part.id));
  useEffect(() => {
    setDraft(saved ? { ...saved } : blank(part.id));
  }, [saved, part.id]);
  const set = <K extends keyof PartReplenishmentDTO>(name: K, value: PartReplenishmentDTO[K]) =>
    setDraft((current) => ({ ...current, [name]: value }));

  const effectivePolicy = draft.replenishment_policy ?? detail.data?.group_policy ?? "AUTO";
  // Compared and saved in the shape the server keeps: a figure the screen no
  // longer shows (the fixed pair off FIXED, the reason and date with no
  // override) is dropped rather than left to fail a constraint unseen.
  const normalized = normalize(draft, effectivePolicy);
  const dirty = JSON.stringify(normalized) !== JSON.stringify(normalize(saved ?? blank(part.id), effectivePolicy));
  // Blank or 0 is no override: planning on nothing is what the NOT_PLANNED
  // policy is for, and the reason and date only mean something beside a usage.
  const hasOverride = (draft.average_daily_usage_override ?? 0) > 0;
  const today = isoDate(new Date());
  const overrideEnded = hasOverride && draft.average_daily_usage_override_end_date !== null && draft.average_daily_usage_override_end_date < today;
  const errors: Record<string, string> = {};
  const negative = t("part-replenishment.error.negative", "Cannot be negative.");
  if (draft.safety_days !== null && (draft.safety_days < 0 || !Number.isInteger(draft.safety_days))) {
    errors.safety = t("part-replenishment.error.safety-days", "A whole number of days, 0 or more.");
  }
  if ((draft.average_daily_usage_override ?? 0) < 0) errors.adu = negative;
  if (hasOverride && !draft.average_daily_usage_override_reason?.trim()) {
    errors.reason = t("part-replenishment.error.reason", "Say why the usage is overridden.");
  }
  if (hasOverride && !draft.average_daily_usage_override_end_date) {
    errors.end = t("part-replenishment.error.end", "An override ends: give it a date.");
  }
  if (effectivePolicy === "FIXED") {
    if ((draft.fixed_minimum ?? 0) < 0 || (draft.fixed_maximum ?? 0) < 0) {
      errors.fixed = negative;
    } else if ((draft.fixed_minimum === null) !== (draft.fixed_maximum === null)) {
      errors.fixed = t("part-replenishment.error.fixed-pair", "A fixed minimum and maximum go together.");
    } else if (draft.fixed_minimum !== null && draft.fixed_maximum !== null && draft.fixed_maximum < draft.fixed_minimum) {
      errors.fixed = t("part-replenishment.error.fixed-order", "The maximum cannot be below the minimum.");
    }
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    await queryClient.invalidateQueries({ queryKey: replenishmentKeys.summary });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (saved) await overrides.update({ ...normalized, id: saved.id });
      else await overrides.create(normalized);
    },
    onSuccess: async () => {
      await refresh();
      toast.success(t("part-replenishment.saved", "Saved. It applies from the next run."));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const reset = useMutation({
    mutationFn: async () => {
      if (saved) await overrides.remove(saved.id);
    },
    onSuccess: async () => {
      await refresh();
      toast.success(t("part-replenishment.reset-done", "Back to its group's settings from the next run."));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const run = useMutation({
    mutationFn: () => replenishment.run(),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: replenishmentKeys.all });
      toast.success(
        t("part-replenishment.ran", "Replenishment recalculated: {{count}} materials in {{seconds}} s", {
          count: result.material_count,
          seconds: (result.duration_ms / 1000).toFixed(1),
        }),
      );
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const group = detail.data;
  // "Follow the group" is the Select's placeholder, its blank entry: listed
  // as an option of its own it would sit beside an empty one, and the empty
  // one would be what shows.
  const follow = (value: string | null) =>
    value
      ? t("part-replenishment.follow-group", "Its group's ({{value}})", { value })
      : t("part-replenishment.follow-group-plain", "Its group's");
  const followPolicy = follow(group?.group_policy ? policyLabel(group.group_policy, t) : null);
  const policyOptions = REPLENISHMENT_POLICIES.map((policy) => ({ value: policy, label: policyLabel(policy, t) }));
  const sourceLabel = (source: UsageSource) =>
    source === "DERIVED" ? t("part-replenishment.source.derived", "Derived from finished work") : t("part-replenishment.source.recorded", "Recorded issues");
  const followSource = follow(group?.group_usage_source ? sourceLabel(group.group_usage_source) : null);
  const sourceOptions = USAGE_SOURCES.map((source) => ({ value: source, label: sourceLabel(source) }));
  const numberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[minmax(0,1fr)_22rem] lg:p-6">
      <div className="min-w-0">
        <MaterialStory partId={part.id} />
      </div>
      <div className="flex flex-col gap-3">
        <Card className="flex flex-col gap-3 p-4">
          <div>
            <h3 className="text-[14px] font-semibold text-ink">{t("part-replenishment.title", "This part's settings")}</h3>
            <p className="text-[12px] text-ink-muted">
              {t("part-replenishment.hint", "Blank follows the part's group. Changes apply at the next run.")}
            </p>
          </div>
          <Field label={t("part-replenishment.policy", "Policy")} htmlFor="pr-policy">
            <Select
              id="pr-policy"
              value={draft.replenishment_policy ?? ""}
              disabled={locked}
              placeholder={followPolicy}
              options={policyOptions}
              onChange={(event) => set("replenishment_policy", (event.target.value || null) as ReplenishmentPolicy | null)}
            />
          </Field>
          <Field label={t("part-replenishment.usage-source", "Usage from")} htmlFor="pr-source">
            <Select
              id="pr-source"
              value={draft.usage_source ?? ""}
              disabled={locked}
              placeholder={followSource}
              options={sourceOptions}
              onChange={(event) => set("usage_source", (event.target.value || null) as UsageSource | null)}
            />
          </Field>
          <Field
            label={t("part-replenishment.safety-days", "Safety days")}
            htmlFor="pr-safety"
            error={errors.safety}
            hint={t("part-replenishment.safety-days.hint", "Group: {{days}} days, +{{extra}} when it feeds top sellers.", {
              days: group?.group_safety_days ?? "",
              extra: group?.group_strategic_extra_days ?? 0,
            })}
          >
            <Input
              id="pr-safety"
              type="number"
              min={0}
              step={1}
              disabled={locked}
              placeholder={String(group?.group_safety_days ?? "")}
              value={draft.safety_days ?? ""}
              onChange={(event) => set("safety_days", numberOrNull(event.target.value))}
            />
          </Field>
          {effectivePolicy === "FIXED" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("part-replenishment.fixed-minimum", "Minimum")} htmlFor="pr-min" error={errors.fixed}>
                <Input
                  id="pr-min"
                  type="number"
                  min={0}
                  step="any"
                  disabled={locked}
                  value={draft.fixed_minimum ?? ""}
                  onChange={(event) => set("fixed_minimum", numberOrNull(event.target.value))}
                />
              </Field>
              <Field label={t("part-replenishment.fixed-maximum", "Maximum")} htmlFor="pr-max">
                <Input
                  id="pr-max"
                  type="number"
                  min={0}
                  step="any"
                  disabled={locked}
                  value={draft.fixed_maximum ?? ""}
                  onChange={(event) => set("fixed_maximum", numberOrNull(event.target.value))}
                />
              </Field>
            </div>
          )}
          <div className="flex flex-col gap-3 rounded-md border border-line-soft bg-surface-sunk/50 p-3">
            <Field
              label={t("part-replenishment.adu-override", "Daily usage override")}
              htmlFor="pr-adu"
              error={errors.adu}
              hint={t("part-replenishment.adu-override.hint", "For a launch or a promotion the history cannot know about. Ends on its date. Blank or 0: no override.")}
            >
              <Input
                id="pr-adu"
                type="number"
                min={0}
                step="any"
                disabled={locked}
                value={draft.average_daily_usage_override ?? ""}
                onChange={(event) => set("average_daily_usage_override", numberOrNull(event.target.value))}
              />
            </Field>
            {hasOverride && (
              <>
                <Field label={t("part-replenishment.adu-reason", "Why")} htmlFor="pr-reason" required error={errors.reason}>
                  <Input
                    id="pr-reason"
                    disabled={locked}
                    value={draft.average_daily_usage_override_reason ?? ""}
                    onChange={(event) => set("average_daily_usage_override_reason", event.target.value === "" ? null : event.target.value)}
                  />
                </Field>
                <Field
                  label={t("part-replenishment.adu-end", "Until")}
                  htmlFor="pr-end"
                  required
                  error={errors.end}
                  hint={overrideEnded ? t("part-replenishment.adu-ended", "This date has passed: the measured usage applies again.") : undefined}
                >
                  <Input
                    id="pr-end"
                    type="date"
                    disabled={locked}
                    value={draft.average_daily_usage_override_end_date ?? ""}
                    onChange={(event) => set("average_daily_usage_override_end_date", event.target.value === "" ? null : event.target.value)}
                  />
                </Field>
              </>
            )}
          </div>
          <Field label={t("column.note", "Note")} htmlFor="pr-note">
            <Textarea id="pr-note" rows={2} disabled={locked} value={draft.note ?? ""} onChange={(event) => set("note", event.target.value === "" ? null : event.target.value)} />
          </Field>
          {!readOnly && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!saved}
                loading={reset.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: t("part-replenishment.reset.title", "Follow the group again?"),
                    message: t("part-replenishment.reset.message", "This part's own settings are removed; it follows {{group}} from the next run.", {
                      group: t("part-replenishment.its-group", "its group"),
                    }),
                    confirmLabel: t("part-replenishment.reset", "Reset to group"),
                  });
                  if (ok) reset.mutate();
                }}
              >
                {t("part-replenishment.reset", "Reset to group")}
              </Button>
              <Button variant="primary" size="sm" disabled={!dirty || Object.keys(errors).length > 0} loading={save.isPending} onClick={() => save.mutate()}>
                {t("button.save", "Save")}
              </Button>
            </div>
          )}
        </Card>
        {!readOnly && (
          <Button
            variant="secondary"
            loading={run.isPending}
            // The run reads saved settings: running with edits on screen would
            // show a plan that ignores them.
            disabled={dirty}
            title={dirty ? t("part-replenishment.recalculate.save-first", "Save first: the run uses saved settings.") : undefined}
            onClick={() => run.mutate()}
          >
            <RefreshCw className="size-4" />
            {t("part-replenishment.recalculate", "Recalculate now")}
          </Button>
        )}
      </div>
    </div>
  );
}

/** The draft as it is saved: no override without a usage, no fixed pair off FIXED, trimmed text. */
function normalize(draft: PartReplenishmentDTO, effectivePolicy: ReplenishmentPolicy): PartReplenishmentDTO {
  const override = draft.average_daily_usage_override;
  const hasOverride = override !== null && override > 0;
  const text = (value: string | null) => (value === null || value.trim() === "" ? null : value.trim());
  return {
    ...draft,
    fixed_minimum: effectivePolicy === "FIXED" ? draft.fixed_minimum : null,
    fixed_maximum: effectivePolicy === "FIXED" ? draft.fixed_maximum : null,
    average_daily_usage_override: hasOverride ? override : null,
    average_daily_usage_override_reason: hasOverride ? text(draft.average_daily_usage_override_reason) : null,
    average_daily_usage_override_end_date: hasOverride ? draft.average_daily_usage_override_end_date : null,
    note: text(draft.note),
  };
}

function blank(partId: number): PartReplenishmentDTO {
  return {
    id: 0,
    part_id: partId,
    replenishment_policy: null,
    usage_source: null,
    safety_days: null,
    fixed_minimum: null,
    fixed_maximum: null,
    average_daily_usage_override: null,
    average_daily_usage_override_reason: null,
    average_daily_usage_override_end_date: null,
    note: null,
  };
}
